import type {
  Environment as ProtoEnvironment,
  Task as ProtoTask,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import type { Timestamp as ProtoTimestamp } from "@bufbuild/protobuf";
import type { GPUInstanceType } from "types/InstanceTypes";
import type {
  Environment as UIEnvironment,
  Status as UIInstanceStatus,
  Task as UITask,
  Timestamp as UITimestamp,
} from "components/Environment/utils/types";
import { getInstanceDiskSize, mapInstanceType } from "../instanceTypes/mappers";

type UIIngressGateway = NonNullable<UIEnvironment["ingress_gateways"]>[number];
type UIIngress = UIIngressGateway["ingresses"][number];
type UIAccessPolicy = UIIngress["access_policies"][number];
type UIIngressGatewayInstallation = UIIngressGateway["installations"][number];

function mapTimestamp(timestamp?: ProtoTimestamp): UITimestamp | undefined {
  if (!timestamp) return undefined;
  return {
    seconds: Number(timestamp.seconds),
    nanos: timestamp.nanos,
  };
}

function mapEnvironmentInstanceType(
  instanceType: NonNullable<ProtoEnvironment["instanceType"]>,
): GPUInstanceType {
  return mapInstanceType(instanceType);
}

function mapTask(task: ProtoTask): UITask {
  return {
    task_id: task.taskId,
    create_time: mapTimestamp(task.createTime),
    finish_time: mapTimestamp(task.finishTime),
    name: task.name,
    status: task.status,
    message: task.message,
    labels: task.labels,
  };
}

function mapInstanceStatus(
  status: NonNullable<NonNullable<ProtoEnvironment["instance"]>["status"]>,
): UIInstanceStatus {
  return {
    object_status: status.objectStatus,
    lifecycle_status:
      status.lifecycleStatus as UIInstanceStatus["lifecycle_status"],
  };
}

function mapIngressGateway(
  gateway: ProtoEnvironment["ingressGateways"][number],
): UIIngressGateway {
  return {
    ingress_gateway_id: gateway.ingressGatewayId,
    create_time: mapTimestamp(gateway.createTime),
    update_time: mapTimestamp(gateway.updateTime),
    ingresses: gateway.ingresses.map(
      (ingress): UIIngress => ({
        ingress_id: ingress.ingressId,
        hostname: ingress.hostname,
        path: ingress.path || undefined,
        service_type: ingress.serviceType,
        port: ingress.port,
        service_hostname: ingress.serviceHostname || undefined,
        access_policies: ingress.accessPolicies.map(
          (policy): UIAccessPolicy => ({
            access_policy_id: policy.accessPolicyId,
            access_policy: policy.accessPolicy
              ? {
                  name: policy.accessPolicy.name || undefined,
                  external_auth_id: policy.accessPolicy.externalAuthId,
                  identity_provider_id:
                    policy.accessPolicy.identityProviderId || undefined,
                  ingress_gateway_token_id:
                    policy.accessPolicy.ingressGatewayTokenId || undefined,
                  is_public: policy.accessPolicy.isPublic,
                }
              : undefined,
            create_time: mapTimestamp(policy.createTime),
          }),
        ),
        create_time: mapTimestamp(ingress.createTime),
        labels: ingress.labels,
      }),
    ),
    labels: gateway.labels,
    external_id: gateway.externalId,
    status: gateway.status as UIIngressGateway["status"],
    installations: gateway.installations.map(
      (installation): UIIngressGatewayInstallation => ({
        ingress_gateway_installation_id:
          installation.ingressGatewayInstallationId || undefined,
        ingress_gateway_id: installation.ingressGatewayId || undefined,
        installation_reference_id:
          installation.installationReferenceId || undefined,
        create_time: mapTimestamp(installation.createTime),
        update_time: mapTimestamp(installation.updateTime),
        labels: installation.labels,
      }),
    ),
  };
}

export function mapEnvironment(environment: ProtoEnvironment): UIEnvironment {
  const instance = environment.instance;
  return {
    workspaceId: environment.environmentId,
    environment_id: environment.environmentId,
    create_time: mapTimestamp(environment.createTime),
    update_time: mapTimestamp(environment.updateTime),
    labels: {
      ...environment.labels,
      organizationId: environment.labels.organizationId ?? "",
      userId: environment.labels.userId ?? "",
    },
    namespace: environment.namespace,
    name: environment.name,
    meta: environment.meta
      ? {
          title: environment.meta.title,
          description: environment.meta.description,
          icon_url: environment.meta.iconUrl,
          meta: environment.meta.meta,
        }
      : undefined,
    instance: instance
      ? {
          instance_id: instance.instanceId,
          create_time: mapTimestamp(instance.createTime),
          update_time: mapTimestamp(instance.updateTime),
          cloud_cred_id: instance.cloudCredId,
          status: instance.status
            ? mapInstanceStatus(instance.status)
            : undefined,
          hostname: instance.hostname,
          location: instance.location,
          image_id: instance.imageId,
          instance_type: instance.instanceType,
          disk_size: getInstanceDiskSize(instance),
          provider_instance_id: instance.providerInstanceId,
          labels: instance.labels,
          public_dns: instance.publicDns,
          public_ip: instance.publicIp,
          retire_timeout: instance.retireTimeout,
          retire_time: mapTimestamp(instance.retireTime),
          ssh_user: instance.sshUser,
          ssh_port: instance.sshPort,
          public_key: instance.publicKey,
          sub_location: instance.subLocation,
          is_stoppable: instance.isStoppable,
          is_rebootable: instance.isRebootable,
          additional_disks: instance.additionalDisks.map((disk) => ({
            size: disk.size,
            type: disk.type,
            mount_path: disk.mountPath,
          })),
          internal_port_mappings: instance.internalPortMappings.map(
            (mapping) => ({
              from_port: mapping.fromPort,
              to_port: mapping.toPort,
            }),
          ),
          instance_type_info: instance.instanceTypeInfo
            ? mapEnvironmentInstanceType(instance.instanceTypeInfo)
            : undefined,
          namespace: instance.namespace,
          name: instance.name,
        }
      : undefined,
    instance_type: environment.instanceType
      ? mapEnvironmentInstanceType(environment.instanceType)
      : undefined,
    build: environment.build?.toJson({
      emitDefaultValues: true,
      useProtoFieldName: true,
    }) as unknown as UIEnvironment["build"],
    ingress_gateways: environment.ingressGateways.map(mapIngressGateway),
    tasks: environment.tasks.map(mapTask),
    status: environment.status,
  };
}
